/**
 * Lift2Fit — the coaching workspace · Packs.
 *
 * The coach side of migrations 0020 and 0021. One screen:
 *
 *   * the **coaching packs** — online, in-person and semi-coaching, each with
 *     its price/cadence, people, coaches, session length, how many people share
 *     a slot, the sessions a member gets per period, the booking windows its
 *     members may use, inclusions and an optional place. Full CRUD; a pack with
 *     subscribers or slots is archived, never deleted.
 *   * the **oversubscription signal** — how many slots are allocated to a pack
 *     and how many members are subscribed, with a plain-words warning when the
 *     demand exceeds the places. A warning, never a refusal.
 *
 * The public profile lives on the coach's own Profile & settings screen
 * (`#/crm/profile`); this screen links to it. A pack can also be offered on an
 * invite, so the invitee reads the same pack the discovery page shows.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import {
  api,
  COACH_PACK_CADENCES,
  COACH_PACK_KINDS,
  type CoachOffer,
  type CoachOfferInput,
  type CoachPackCadence,
  type CoachPackKind,
  type CoachPackOwner,
} from '@/lib/api';
import { Button } from '@/components/ui';
import { LiveMessage, Panel, ScreenHeader, StatusChip } from '../components';
import { CrmMessage, formatMoney } from './shared';
import { inviteShareLink } from '../coach';
import { useAsync } from '../hooks';
import { useSession } from '@/lib/useSession';
import { ArrowRightIcon } from '@/lib/icons';
import { Link } from '../router';

const KIND_LABELS: Record<CoachPackKind, string> = {
  online: 'Online',
  in_person: 'In person',
  semi_coaching: 'Semi-coaching',
};

const CADENCE_LABELS: Record<CoachPackCadence, string> = {
  one_off: 'One-off',
  weekly: 'Weekly',
  monthly: 'Monthly',
  pay_as_go: 'Pay as you go (hourly)',
};

const WEEKDAYS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

function splitList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function listText(values: readonly string[]): string {
  return values.join(', ');
}

/** "HH:MM" → minutes from midnight. */
function toMinutes(value: string): number {
  const [hours, minutes] = value.split(':');
  const h = Number(hours);
  const m = Number(minutes);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return Math.min(1439, Math.max(0, h * 60 + m));
}

/** minutes from midnight → "HH:MM". */
function toClock(value: number): string {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

interface WindowDraft {
  weekday: number;
  start: string;
  end: string;
}

interface PackDraft {
  name: string;
  kind: CoachPackKind;
  description: string;
  price: string;
  cadence: CoachPackCadence;
  capacity: string;
  coachCount: string;
  sessionMinutes: string;
  slotCapacity: string;
  sessionsPerPeriod: string;
  windows: WindowDraft[];
  inclusions: string;
  location: string;
}

const EMPTY_PACK: PackDraft = {
  name: '',
  kind: 'online',
  description: '',
  price: '',
  cadence: 'monthly',
  capacity: '1',
  coachCount: '1',
  sessionMinutes: '60',
  slotCapacity: '1',
  sessionsPerPeriod: '1',
  windows: [],
  inclusions: '',
  location: '',
};

function draftFromPack(pack: CoachPackOwner): PackDraft {
  return {
    name: pack.name,
    kind: pack.kind,
    description: pack.description ?? '',
    price: String(pack.price),
    cadence: pack.cadence,
    capacity: String(pack.capacity),
    coachCount: String(pack.coachCount),
    sessionMinutes: String(pack.sessionMinutes),
    slotCapacity: String(pack.slotCapacity),
    sessionsPerPeriod: String(pack.sessionsPerPeriod),
    windows: pack.windows.map((window) => ({
      weekday: window.weekday,
      start: toClock(window.startMinute),
      end: toClock(window.endMinute),
    })),
    inclusions: listText(pack.inclusions),
    location: pack.location ?? '',
  };
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

export function CrmPacksScreen() {
  const bundle = useAsync(() => api.coachProfileBundle(), []);
  const profile = bundle.data?.profile ?? null;
  const packs = bundle.data?.packs ?? [];
  const session = useSession();
  // 0034: packs are a paid feature. A free account gets the reason and the way
  // out instead of a "New pack" button the API would refuse.
  const packsIncluded = session.user === null || session.user.coachPlan !== 'free';

  const [packDraft, setPackDraft] = useState<PackDraft>(EMPTY_PACK);
  const [editingPackId, setEditingPackId] = useState<string | null>(null);
  const [packFormOpen, setPackFormOpen] = useState(false);
  const [packBusy, setPackBusy] = useState<string | null>(null);
  const [packMessage, setPackMessage] = useState<string | null>(null);
  const [packTone, setPackTone] = useState<'info' | 'success' | 'danger'>('info');
  const [offerPackId, setOfferPackId] = useState<string | null>(null);
  const [offerEmail, setOfferEmail] = useState('');
  const [offerInvite, setOfferInvite] = useState<{ packId: string; code: string; url: string } | null>(null);

  const openCreate = () => {
    setEditingPackId(null);
    setPackDraft(EMPTY_PACK);
    setPackFormOpen(true);
    setPackMessage(null);
  };

  const openEdit = (pack: CoachPackOwner) => {
    setEditingPackId(pack.packId);
    setPackDraft(draftFromPack(pack));
    setPackFormOpen(true);
    setPackMessage(null);
  };

  const submitPack = async (event: FormEvent) => {
    event.preventDefault();
    const capacity = Math.max(1, Math.round(Number(packDraft.capacity) || 1));
    setPackBusy('save');
    setPackTone('info');
    setPackMessage('Saving…');
    const input = {
      name: packDraft.name.trim(),
      kind: packDraft.kind,
      description: packDraft.description.trim() === '' ? null : packDraft.description.trim(),
      price: Number(packDraft.price),
      currency: 'GBP',
      cadence: packDraft.cadence,
      capacity,
      coachCount: Math.max(1, Math.round(Number(packDraft.coachCount) || 1)),
      sessionMinutes: Math.max(15, Math.round(Number(packDraft.sessionMinutes) || 60)),
      slotCapacity: Math.min(capacity, Math.max(1, Math.round(Number(packDraft.slotCapacity) || 1))),
      sessionsPerPeriod: Math.max(1, Math.round(Number(packDraft.sessionsPerPeriod) || 1)),
      windows: packDraft.windows
        .map((window) => ({
          weekday: window.weekday,
          startMinute: toMinutes(window.start),
          endMinute: toMinutes(window.end),
        }))
        .filter((window) => window.endMinute > window.startMinute),
      inclusions: splitList(packDraft.inclusions),
      location: packDraft.location.trim() === '' ? null : packDraft.location.trim(),
    };
    const result = editingPackId
      ? await api.updateCoachPack(editingPackId, input)
      : await api.createCoachPack(input);
    setPackBusy(null);
    if (result.problem || !result.data) {
      setPackTone('danger');
      setPackMessage(result.problem?.detail ?? 'The pack did not save.');
      return;
    }
    setPackFormOpen(false);
    setEditingPackId(null);
    setPackTone('success');
    setPackMessage(`Saved "${result.data.name}".`);
    bundle.reload();
  };

  const archivePack = async (pack: CoachPackOwner) => {
    setPackBusy(pack.packId);
    const result = await api.archiveCoachPack(pack.packId);
    setPackBusy(null);
    if (result.problem || !result.data) {
      setPackTone('danger');
      setPackMessage(result.problem?.detail ?? 'The pack was not archived.');
      return;
    }
    setPackTone('success');
    setPackMessage(`"${pack.name}" archived — hidden from discovery, subscribers keep access.`);
    bundle.reload();
  };

  const restorePack = async (pack: CoachPackOwner) => {
    setPackBusy(pack.packId);
    const result = await api.restoreCoachPack(pack.packId);
    setPackBusy(null);
    if (result.problem || !result.data) {
      setPackTone('danger');
      setPackMessage(result.problem?.detail ?? 'The pack was not restored.');
      return;
    }
    setPackTone('success');
    setPackMessage(`"${pack.name}" is live again.`);
    bundle.reload();
  };

  const deletePack = async (pack: CoachPackOwner) => {
    setPackBusy(pack.packId);
    const result = await api.deleteCoachPack(pack.packId);
    setPackBusy(null);
    if (result.problem || !result.data) {
      setPackTone('danger');
      setPackMessage(result.problem?.detail ?? 'The pack was not deleted.');
      return;
    }
    setPackTone('success');
    setPackMessage(`"${pack.name}" deleted.`);
    bundle.reload();
  };

  const offerPack = async (pack: CoachPackOwner, event: FormEvent) => {
    event.preventDefault();
    const email = offerEmail.trim();
    if (email === '') return;
    setPackBusy(pack.packId);
    setPackTone('info');
    setPackMessage('Creating the invite…');
    const result = await api.inviteCoach({
      traineeEmail: email,
      packId: pack.packId,
      sharedFields: ['sessions', 'sets'],
    });
    setPackBusy(null);
    if (result.problem || !result.data) {
      setPackTone('danger');
      setPackMessage(result.problem?.detail ?? 'The invite could not be created.');
      return;
    }
    setOfferInvite({ packId: pack.packId, code: result.data.code, url: inviteShareLink(result.data) });
    setPackTone('success');
    setPackMessage(`Invite sent to ${email} — it offers "${pack.name}".`);
  };

  if (!packsIncluded) {
    return (
      <div className="flex flex-col gap-6" data-testid="crm-packs">
        <ScreenHeader
          eyebrow="Coaching workspace"
          title="Packs"
          lead="What you offer clients: one price, the sessions it includes and when they can book."
        />
        <Panel testId="crm-packs-upgrade" title="Packs are part of a paid plan">
          <p className="text-[14px] text-ink-muted">
            This account has no coaching workspace yet, so there is nothing to sell. The Coach plan
            (£19.99 / month, 10 clients) includes packs, discovery and your own lifter profile; Gyms
            (£49) adds internal coaches. If you only want to train, a free lifter account does that.
          </p>
          <Link
            to="/for-coaches#crm-pricing"
            data-testid="crm-packs-upgrade-link"
            className="inline-flex h-11 w-fit items-center gap-2 border border-accent px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-accent hover:bg-accent hover:text-on-ink-inverse"
          >
            See the plans <ArrowRightIcon />
          </Link>
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="crm-packs">
      <ScreenHeader
        eyebrow="Coaching workspace"
        title="Packs"
        lead="What a lifter signs up for and books: the price and cadence, the session length, when its members may book, and how many share a slot."
        actions={
          <Link
            to="/crm/profile"
            data-testid="crm-packs-profile-link"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            Public profile &amp; places
          </Link>
        }
      />

      <Panel
        testId="crm-coach-packs"
        title="Coaching packs"
        subtitle={
          profile
            ? `Your public profile is ${profile.discoverable ? 'listed in discovery' : 'hidden from discovery'}. A pack with subscribers or slots is archived, never deleted.`
            : 'A pack with subscribers or slots is archived, never deleted.'
        }
        actions={
          <Button variant="secondary" size="sm" data-testid="crm-pack-new" onClick={openCreate}>
            New pack
          </Button>
        }
      >
        {packFormOpen ? (
          <form
            className="flex flex-col gap-3 border border-accent bg-page-subtle p-3"
            onSubmit={submitPack}
            data-testid="crm-pack-form"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Pack name">
                <input
                  type="text"
                  data-testid="crm-pack-name"
                  value={packDraft.name}
                  onChange={(event) => setPackDraft({ ...packDraft, name: event.target.value })}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </Field>
              <Field label="Kind">
                <select
                  data-testid="crm-pack-kind"
                  value={packDraft.kind}
                  onChange={(event) => setPackDraft({ ...packDraft, kind: event.target.value as CoachPackKind })}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                >
                  {COACH_PACK_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {KIND_LABELS[kind]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Price (GBP)">
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  data-testid="crm-pack-price"
                  value={packDraft.price}
                  onChange={(event) => setPackDraft({ ...packDraft, price: event.target.value })}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </Field>
              <Field label="Billing cadence">
                <select
                  data-testid="crm-pack-cadence"
                  value={packDraft.cadence}
                  onChange={(event) =>
                    setPackDraft({ ...packDraft, cadence: event.target.value as CoachPackCadence })
                  }
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                >
                  {COACH_PACK_CADENCES.map((cadence) => (
                    <option key={cadence} value={cadence}>
                      {CADENCE_LABELS[cadence]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="People (pack size)">
                <input
                  type="number"
                  min={1}
                  max={500}
                  data-testid="crm-pack-capacity"
                  value={packDraft.capacity}
                  onChange={(event) =>
                    setPackDraft({
                      ...packDraft,
                      capacity: event.target.value,
                      slotCapacity:
                        Number(packDraft.slotCapacity) > Number(event.target.value)
                          ? event.target.value
                          : packDraft.slotCapacity,
                    })
                  }
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </Field>
              <Field label="Coaches on it">
                <input
                  type="number"
                  min={1}
                  max={100}
                  data-testid="crm-pack-coach-count"
                  value={packDraft.coachCount}
                  onChange={(event) => setPackDraft({ ...packDraft, coachCount: event.target.value })}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </Field>
              <Field label="Session length (minutes)">
                <input
                  type="number"
                  min={15}
                  max={480}
                  data-testid="crm-pack-session-minutes"
                  value={packDraft.sessionMinutes}
                  onChange={(event) => setPackDraft({ ...packDraft, sessionMinutes: event.target.value })}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </Field>
              <Field label="People share a slot">
                <input
                  type="number"
                  min={1}
                  max={500}
                  data-testid="crm-pack-slot-capacity"
                  value={packDraft.slotCapacity}
                  onChange={(event) => setPackDraft({ ...packDraft, slotCapacity: event.target.value })}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </Field>
              <Field label="Sessions per member per period">
                <input
                  type="number"
                  min={1}
                  max={100}
                  data-testid="crm-pack-sessions-per-period"
                  value={packDraft.sessionsPerPeriod}
                  onChange={(event) => setPackDraft({ ...packDraft, sessionsPerPeriod: event.target.value })}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </Field>
              <Field label="Place (optional)">
                <input
                  type="text"
                  data-testid="crm-pack-location"
                  value={packDraft.location}
                  onChange={(event) => setPackDraft({ ...packDraft, location: event.target.value })}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                />
              </Field>
            </div>

            <div className="flex flex-col gap-2 border border-hairline bg-page p-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                Booking windows (your timezone)
              </span>
              <p className="text-[12px] text-ink-muted">
                When this pack’s members may book. Add none and its slots are open at any time.
              </p>
              {packDraft.windows.map((window, index) => (
                <div key={index} className="flex flex-wrap items-end gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Day</span>
                    <select
                      data-testid={`crm-pack-window-day-${index}`}
                      value={window.weekday}
                      onChange={(event) =>
                        setPackDraft((current) => ({
                          ...current,
                          windows: current.windows.map((entry, i) =>
                            i === index ? { ...entry, weekday: Number(event.target.value) } : entry,
                          ),
                        }))
                      }
                      className="h-11 border border-hairline bg-page px-2 text-[14px] text-ink"
                    >
                      {WEEKDAYS.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">From</span>
                    <input
                      type="time"
                      data-testid={`crm-pack-window-start-${index}`}
                      value={window.start}
                      onChange={(event) =>
                        setPackDraft((current) => ({
                          ...current,
                          windows: current.windows.map((entry, i) =>
                            i === index ? { ...entry, start: event.target.value } : entry,
                          ),
                        }))
                      }
                      className="h-11 border border-hairline bg-page px-2 text-[14px] text-ink"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">To</span>
                    <input
                      type="time"
                      data-testid={`crm-pack-window-end-${index}`}
                      value={window.end}
                      onChange={(event) =>
                        setPackDraft((current) => ({
                          ...current,
                          windows: current.windows.map((entry, i) =>
                            i === index ? { ...entry, end: event.target.value } : entry,
                          ),
                        }))
                      }
                      className="h-11 border border-hairline bg-page px-2 text-[14px] text-ink"
                    />
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`crm-pack-window-remove-${index}`}
                    onClick={() =>
                      setPackDraft((current) => ({
                        ...current,
                        windows: current.windows.filter((_, i) => i !== index),
                      }))
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="crm-pack-window-add"
                  onClick={() =>
                    setPackDraft((current) => ({
                      ...current,
                      windows: [...current.windows, { weekday: 2, start: '06:00', end: '12:00' }],
                    }))
                  }
                >
                  Add a window
                </Button>
              </div>
            </div>

            <Field label="Description">
              <textarea
                data-testid="crm-pack-description"
                value={packDraft.description}
                onChange={(event) => setPackDraft({ ...packDraft, description: event.target.value })}
                rows={2}
                className="border border-hairline bg-page px-3 py-2 text-[15px] text-ink"
              />
            </Field>
            <Field label="Inclusions (comma separated)">
              <input
                type="text"
                data-testid="crm-pack-inclusions"
                value={packDraft.inclusions}
                onChange={(event) => setPackDraft({ ...packDraft, inclusions: event.target.value })}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" variant="secondary" disabled={packBusy === 'save'} data-testid="crm-pack-submit">
                {editingPackId ? 'Save pack' : 'Create pack'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPackFormOpen(false);
                  setEditingPackId(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : null}

        {packs.length === 0 ? (
          <p className="text-[13px] text-ink-muted" data-testid="crm-packs-empty">
            No packs yet. Create one and a lifter can discover and sign up for it.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {packs.map((pack) => (
              <li
                key={pack.packId}
                data-testid={`crm-pack-${pack.packId}`}
                className="flex flex-col gap-2 border border-hairline px-3 py-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-ink">
                      {pack.name}
                      <StatusChip tone="info">{KIND_LABELS[pack.kind]}</StatusChip>
                      {pack.cadence === 'pay_as_go' ? <StatusChip tone="neutral">Pay as you go</StatusChip> : null}
                      {pack.archivedAt ? <StatusChip tone="neutral">Archived</StatusChip> : null}
                    </span>
                    <span className="text-[12px] text-ink" data-testid={`crm-pack-summary-${pack.packId}`}>
                      {pack.summary}
                    </span>
                    <span className="text-[12px] text-ink-muted">
                      {formatMoney(pack.price, pack.currency)} · {CADENCE_LABELS[pack.cadence]} · {pack.capacity}{' '}
                      {pack.capacity === 1 ? 'person' : 'people'} · {pack.slotCapacity}{' '}
                      {pack.slotCapacity === 1 ? 'shares a slot' : 'share a slot'} · {pack.subscriberCount}{' '}
                      {pack.subscriberCount === 1 ? 'subscriber' : 'subscribers'} ·{' '}
                      {pack.oversubscription.allocatedSlots} upcoming{' '}
                      {pack.oversubscription.allocatedSlots === 1 ? 'slot' : 'slots'}
                    </span>
                    {pack.inclusions.length ? (
                      <span className="text-[12px] text-ink-muted">Includes {listText(pack.inclusions)}.</span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!pack.archivedAt ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={packBusy === pack.packId}
                          data-testid={`crm-pack-edit-${pack.packId}`}
                          onClick={() => openEdit(pack)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={packBusy === pack.packId}
                          data-testid={`crm-pack-offer-${pack.packId}`}
                          onClick={() => {
                            setOfferPackId(offerPackId === pack.packId ? null : pack.packId);
                            setOfferEmail('');
                            setOfferInvite(null);
                          }}
                        >
                          Offer to a lifter
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={packBusy === pack.packId}
                          data-testid={`crm-pack-archive-${pack.packId}`}
                          onClick={() => void archivePack(pack)}
                        >
                          Archive
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={packBusy === pack.packId}
                        data-testid={`crm-pack-restore-${pack.packId}`}
                        onClick={() => void restorePack(pack)}
                      >
                        Restore
                      </Button>
                    )}
                    {pack.subscriberCount === 0 ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={packBusy === pack.packId}
                        data-testid={`crm-pack-delete-${pack.packId}`}
                        onClick={() => void deletePack(pack)}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </div>
                </div>

                {/* Oversubscription is a warning, never a refusal: the coach may
                    be mid-setup, and a hard block would strand the subscribers
                    already signed up. */}
                {pack.oversubscription.oversubscribed ? (
                  <p
                    data-testid={`crm-pack-oversubscribed-${pack.packId}`}
                    className="border-l-2 border-l-warning bg-page-subtle px-3 py-2 text-[12px] text-ink"
                  >
                    <span className="font-bold uppercase tracking-[0.06em]">Heads up:</span>{' '}
                    {pack.oversubscription.reason} Add slots to this pack or raise the sharing per slot.
                  </p>
                ) : null}

                {offerPackId === pack.packId ? (
                  <form
                    className="flex flex-col gap-2 border-t border-hairline pt-2"
                    onSubmit={(event) => void offerPack(pack, event)}
                    data-testid={`crm-pack-offer-form-${pack.packId}`}
                  >
                    <p className="text-[12px] text-ink-muted">
                      The invite carries this pack: the lifter sees its name, kind, price and capacity before accepting.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="email"
                        required
                        value={offerEmail}
                        onChange={(event) => setOfferEmail(event.target.value)}
                        placeholder="lifter@example.com"
                        data-testid={`crm-pack-offer-email-${pack.packId}`}
                        className="h-10 min-w-[220px] flex-1 border border-hairline bg-page px-3 text-[14px] text-ink"
                      />
                      <Button
                        type="submit"
                        size="sm"
                        variant="secondary"
                        disabled={packBusy === pack.packId}
                        data-testid={`crm-pack-offer-submit-${pack.packId}`}
                      >
                        Send invite
                      </Button>
                    </div>
                    {offerInvite?.packId === pack.packId ? (
                      <p className="text-[12px] text-ink" data-testid={`crm-pack-offer-code-${pack.packId}`}>
                        Invite ready: <span className="font-mono font-bold">{offerInvite.code}</span>
                      </p>
                    ) : null}
                  </form>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <CrmMessage message={packMessage} tone={packTone} testId="crm-packs-message" />
      </Panel>

      <OffersPanel packs={packs.filter((pack) => pack.archivedAt === null)} />
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Offers and referrals (0039)                                               */
/* ------------------------------------------------------------------------- */

const EMPTY_OFFER = {
  kind: 'referral' as 'referral' | 'time_limited',
  name: '',
  packId: '',
  percentOff: '30',
  months: '3',
  code: '',
  referrerPercentOff: '25',
  referrerMonths: '1',
  endsAt: '',
  maxRedemptions: '',
};

/**
 * The coach's offers, one list, with the two kinds side by side because they are
 * the same decision seen twice:
 *
 *   * a time-limited discount is advertised WITH the pack ("30% off the first
 *     three months, until 30 November") and needs no code;
 *   * a referral is a code with two sides — the new client's discount and the
 *     reward for the client who shared it. The link a client copies carries
 *     their own id in `ref`, which is how the share gets credited.
 *
 * Deleting is only offered for an offer nobody used; one that has been redeemed
 * is paused instead, because the discount it gave is a fact about a payment.
 */
function OffersPanel({ packs }: { packs: CoachPackOwner[] }) {
  const offers = useAsync(() => api.coachOffers(), []);
  const redemptions = useAsync(() => api.offerRedemptions(), []);
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_OFFER });
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');

  const list = offers.data ?? [];
  const used = redemptions.data ?? [];

  const reload = () => {
    offers.reload();
    redemptions.reload();
  };

  const submit = async () => {
    const percentOff = Number.parseInt(draft.percentOff, 10);
    if (!Number.isFinite(percentOff) || percentOff < 1 || percentOff > 100) {
      setTone('danger');
      setNotice('A discount is between 1 and 100 per cent.');
      return;
    }
    if (draft.kind === 'referral' && draft.code.trim().length < 3) {
      setTone('danger');
      setNotice('A referral needs a code of at least three characters to share.');
      return;
    }
    const input: CoachOfferInput = {
      packId: draft.packId === '' ? null : draft.packId,
      kind: draft.kind,
      name: draft.name.trim() === '' ? (draft.kind === 'referral' ? 'Refer a friend' : 'Limited-time offer') : draft.name.trim(),
      percentOff,
      months: Math.max(1, Number.parseInt(draft.months, 10) || 1),
    };
    if (draft.kind === 'referral') {
      input.code = draft.code.trim().toUpperCase();
      const referrer = Number.parseInt(draft.referrerPercentOff, 10);
      if (Number.isFinite(referrer) && referrer >= 1 && referrer <= 100) {
        input.referrerPercentOff = referrer;
        input.referrerMonths = Math.max(1, Number.parseInt(draft.referrerMonths, 10) || 1);
      }
    } else {
      input.startsAt = new Date().toISOString();
      if (draft.endsAt !== '') input.endsAt = new Date(`${draft.endsAt}T23:59:59`).toISOString();
    }
    const max = Number.parseInt(draft.maxRedemptions, 10);
    if (Number.isFinite(max) && max > 0) input.maxRedemptions = max;

    setBusy('create');
    const result = await api.createCoachOffer(input);
    setBusy(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setNotice(result.problem?.detail ?? result.problem?.title ?? 'The offer was not created.');
      return;
    }
    setTone('success');
    setNotice(
      result.data.kind === 'referral'
        ? `Code ${result.data.code} is live — share the link below.`
        : `${result.data.name} is live: ${result.data.summary}.`,
    );
    setFormOpen(false);
    setDraft({ ...EMPTY_OFFER });
    reload();
  };

  const toggle = async (offer: CoachOffer) => {
    setBusy(offer.offerId);
    const result = await api.updateCoachOffer({ offerId: offer.offerId, active: !offer.active });
    setBusy(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setNotice(result.problem?.detail ?? 'That offer was not updated.');
      return;
    }
    setTone('info');
    setNotice(`${result.data.name} is now ${result.data.status}.`);
    reload();
  };

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setTone('success');
      setNotice(`${label} copied.`);
    } catch {
      setTone('info');
      setNotice(`Copy this: ${text}`);
    }
  };

  const shareLink = (offer: CoachOffer) => {
    const base = `${window.location.origin}/#/signup`;
    return `${base}?code=${encodeURIComponent(offer.code ?? '')}&ref=<your client id>`;
  };

  return (
    <Panel
      testId="crm-offers"
      title="Offers & referrals"
      subtitle="A discount on a pack: advertised with it for a limited time, or a code a client passes on — where the client who shares it is rewarded too."
      actions={
        <Button variant="secondary" size="sm" data-testid="crm-offer-new" onClick={() => setFormOpen((open) => !open)}>
          {formOpen ? 'Close' : 'New offer'}
        </Button>
      }
    >
      {formOpen ? (
        <form
          className="flex flex-col gap-3 border border-accent bg-page-subtle p-3"
          data-testid="crm-offer-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Kind">
              <select
                data-testid="crm-offer-kind"
                value={draft.kind}
                onChange={(event) =>
                  setDraft({ ...draft, kind: event.target.value === 'time_limited' ? 'time_limited' : 'referral' })
                }
                className="h-10 w-full border border-hairline bg-page px-2 text-[14px]"
              >
                <option value="referral">Referral code — two-sided</option>
                <option value="time_limited">Limited-time discount</option>
              </select>
            </Field>
            <Field label="Pack">
              <select
                data-testid="crm-offer-pack"
                value={draft.packId}
                onChange={(event) => setDraft({ ...draft, packId: event.target.value })}
                className="h-10 w-full border border-hairline bg-page px-2 text-[14px]"
              >
                <option value="">All my live packs</option>
                {packs.map((pack) => (
                  <option key={pack.packId} value={pack.packId}>
                    {pack.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Name">
              <input
                data-testid="crm-offer-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder={draft.kind === 'referral' ? 'Bring a friend' : 'January sale'}
                className="h-10 w-full border border-hairline bg-page px-3 text-[14px] text-ink"
              />
            </Field>
            <Field label="Discount % (first months)">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={100}
                  data-testid="crm-offer-percent"
                  value={draft.percentOff}
                  onChange={(event) => setDraft({ ...draft, percentOff: event.target.value })}
                  className="h-10 w-20 border border-hairline bg-page px-3 text-[14px] text-ink"
                />
                <span className="text-[12px] text-ink-muted">for</span>
                <input
                  type="number"
                  min={1}
                  max={24}
                  data-testid="crm-offer-months"
                  value={draft.months}
                  onChange={(event) => setDraft({ ...draft, months: event.target.value })}
                  className="h-10 w-16 border border-hairline bg-page px-3 text-[14px] text-ink"
                />
                <span className="text-[12px] text-ink-muted">month(s)</span>
              </div>
            </Field>
            {draft.kind === 'referral' ? (
              <>
                <Field label="Code">
                  <input
                    data-testid="crm-offer-code"
                    value={draft.code}
                    onChange={(event) => setDraft({ ...draft, code: event.target.value.toUpperCase() })}
                    placeholder="EARLY30"
                    className="h-10 w-full border border-hairline bg-page px-3 font-mono text-[14px] text-ink"
                  />
                </Field>
                <Field label="Reward for the client who shares">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      data-testid="crm-offer-referrer-percent"
                      value={draft.referrerPercentOff}
                      onChange={(event) => setDraft({ ...draft, referrerPercentOff: event.target.value })}
                      className="h-10 w-20 border border-hairline bg-page px-3 text-[14px] text-ink"
                    />
                    <span className="text-[12px] text-ink-muted">% off their next</span>
                    <input
                      type="number"
                      min={1}
                      max={24}
                      data-testid="crm-offer-referrer-months"
                      value={draft.referrerMonths}
                      onChange={(event) => setDraft({ ...draft, referrerMonths: event.target.value })}
                      className="h-10 w-16 border border-hairline bg-page px-3 text-[14px] text-ink"
                    />
                    <span className="text-[12px] text-ink-muted">month(s)</span>
                  </div>
                </Field>
              </>
            ) : (
              <>
                <Field label="Ends">
                  <input
                    type="date"
                    data-testid="crm-offer-ends"
                    value={draft.endsAt}
                    onChange={(event) => setDraft({ ...draft, endsAt: event.target.value })}
                    className="h-10 w-full border border-hairline bg-page px-3 text-[14px] text-ink"
                  />
                </Field>
                <Field label="Redeemable up to (optional)">
                  <input
                    type="number"
                    min={1}
                    data-testid="crm-offer-max"
                    value={draft.maxRedemptions}
                    onChange={(event) => setDraft({ ...draft, maxRedemptions: event.target.value })}
                    placeholder="no limit"
                    className="h-10 w-full border border-hairline bg-page px-3 text-[14px] text-ink"
                  />
                </Field>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" variant="secondary" disabled={busy === 'create'} data-testid="crm-offer-submit">
              {busy === 'create' ? 'Creating…' : 'Create offer'}
            </Button>
            <p className="text-[12px] text-ink-muted">
              The buyer's first payment is reduced; the pack price they agreed to stays on the subscription.
            </p>
          </div>
        </form>
      ) : null}

      {offers.loading && !offers.data ? (
        <p className="text-[12px] text-ink-muted">Loading offers…</p>
      ) : list.length === 0 ? (
        <p className="text-[13px] text-ink-muted" data-testid="crm-offers-empty">
          No offers yet. A limited-time discount fills a quiet month; a referral code brings a client's
          friend in — and pays the client who brought them.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="crm-offers-list">
          {list.map((offer) => (
            <li
              key={offer.offerId}
              data-testid={`crm-offer-${offer.offerId}`}
              className="flex flex-col gap-2 border border-hairline bg-surface p-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-[14px] font-bold text-ink">{offer.name}</span>
                  <StatusChip tone={offer.status === 'live' ? 'success' : offer.status === 'paused' ? 'neutral' : 'warning'}>
                    {offer.status}
                  </StatusChip>
                  {offer.packName === null ? (
                    <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-muted">all packs</span>
                  ) : (
                    <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-muted">{offer.packName}</span>
                  )}
                </span>
                <span className="text-[12px] text-ink-muted">
                  used {offer.redemptions}
                  {offer.maxRedemptions === null ? '' : ` / ${offer.maxRedemptions}`}
                </span>
              </div>
              <p className="text-[12px] text-ink-muted">{offer.summary}</p>
              {offer.code === null ? null : (
                <div className="flex flex-wrap items-center gap-2">
                  <code className="border border-hairline bg-page-subtle px-2 py-1 font-mono text-[13px] font-bold tracking-[0.08em] text-ink">
                    {offer.code}
                  </code>
                  <Button size="sm" variant="ghost" onClick={() => void copy(offer.code ?? '', 'Code')}>
                    Copy code
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void copy(shareLink(offer), 'Share link')}>
                    Copy share link
                  </Button>
                  <span className="text-[11px] text-ink-muted">
                    a client's own link puts their id in <code>ref</code>, and their reward follows
                  </span>
                </div>
              )}
              {offer.kind === 'referral' && offer.referrerPercentOff === null ? (
                <p className="text-[12px] text-ink-muted">No referrer reward — only the new client benefits.</p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy === offer.offerId}
                  data-testid={`crm-offer-toggle-${offer.offerId}`}
                  onClick={() => void toggle(offer)}
                >
                  {offer.active ? 'Pause' : 'Resume'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {used.length > 0 ? (
        <div className="mt-2 flex flex-col gap-1 border-t border-hairline pt-2" data-testid="crm-offer-redemptions">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-ink-muted">Recent redemptions</p>
          {used.slice(0, 6).map((row) => (
            <p key={row.redemptionId} className="text-[12px] text-ink-muted">
              <span className="font-bold text-ink">{row.newClientName ?? row.newClientEmail}</span> used{' '}
              {row.code ?? row.offerName} — {row.percentOff}% off{row.packName === null ? '' : ` · ${row.packName}`}
              {row.referrerEmail === null
                ? ' · no share credited'
                : row.referrerRewarded
                  ? ` · ${row.referrerName ?? row.referrerEmail} earned ${row.referrerPercentOff ?? 0}%`
                  : ` · shared by ${row.referrerName ?? row.referrerEmail} (no unpaid payment to reduce)`}
            </p>
          ))}
        </div>
      ) : null}

      <CrmMessage message={notice} tone={tone} testId="crm-offers-message" />
    </Panel>
  );
}
